import { useState } from 'react'
import { Terminal, ExternalLink, Calendar, User } from 'lucide-react'
import { useArticles } from '../hooks/useArticles'
import type { Article } from '../types/blog'

/* ─────────────────────────────────────────
   Helpers
   ───────────────────────────────────────── */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const THREAT_ACCENT: Record<string, string> = {
  critical: '#DC143C',
  high:     '#cc7700',
  medium:   '#556B2F',
  low:      '#2a2a2a',
}

const BADGE: Record<string, string> = {
  critical: 'badge-critical text-white font-mono text-[10px] uppercase tracking-widest px-2.5 py-1',
  high:     'badge-high     text-white font-mono text-[10px] uppercase tracking-widest px-2.5 py-1',
  medium:   'badge-medium   text-white font-mono text-[10px] uppercase tracking-widest px-2.5 py-1',
  low:      'badge-low      font-mono text-[10px] uppercase tracking-widest px-2.5 py-1',
}

type ThreatFilter = 'all' | 'critical' | 'high' | 'medium' | 'low'

const FILTER_PILLS: { label: string; value: ThreatFilter }[] = [
  { label: 'ALL', value: 'all' },
  { label: 'CRITICAL', value: 'critical' },
  { label: 'HIGH', value: 'high' },
  { label: 'MEDIUM', value: 'medium' },
  { label: 'LOW', value: 'low' },
]

/* ─────────────────────────────────────────
   Page
   ───────────────────────────────────────── */
export default function Operations() {
  const [threatFilter, setThreatFilter] = useState<ThreatFilter>('all')

  const { articles, total, loading, error } = useArticles({
    category: 'threat-intel',
    threat_level: threatFilter === 'all' ? undefined : threatFilter,
    limit: 20,
  })

  return (
    <div className="bg-terminal-bg min-h-screen">
      <div className="container mx-auto px-4 max-w-7xl py-10">

        {/* ── Header ── */}
        <div className="mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-kgb-red mb-2">
            ★ &nbsp; Threat Intelligence Division
          </p>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-white uppercase tracking-wider mb-3">
            Active Operations
          </h1>
          <p className="text-text-gray font-mono text-sm leading-relaxed max-w-2xl">
            Real-time threat landscape analysis. Monitoring state-sponsored actors, APT groups,
            and emerging cyber threats across global infrastructure.
          </p>
        </div>

        {/* ── Section divider ── */}
        <div className="flex items-center gap-4 mb-6">
          <div className="h-px flex-1 bg-border-gray/50" />
          <div className="flex items-center gap-2">
            <Terminal className="text-kgb-red" size={16} />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-text-gray">
              {loading ? 'SCANNING...' : `${total} ACTIVE THREAT REPORTS`}
            </span>
          </div>
          <div className="h-px flex-1 bg-border-gray/50" />
        </div>

        {/* ── Filter bar ── */}
        <div className="flex flex-wrap gap-2 mb-8">
          {FILTER_PILLS.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => setThreatFilter(value)}
              className={`font-mono text-[10px] uppercase tracking-widest px-4 py-2 border transition-all cursor-pointer ${
                threatFilter === value
                  ? 'bg-kgb-red border-kgb-red text-white'
                  : 'bg-transparent border-border-gray text-text-gray hover:border-kgb-red/50 hover:text-text-light'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="terminal-window p-6 text-center">
            <p className="font-mono text-status-secure text-sm animate-pulse">
              $ SCANNING INTEL FEEDS...<span className="animate-blink">_</span>
            </p>
          </div>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="terminal-window p-6 text-center">
            <p className="font-mono text-kgb-red text-sm">[ERROR] {error}</p>
          </div>
        )}

        {/* ── Grid ── */}
        {!loading && !error && (
          <>
            {articles.length === 0 ? (
              <div className="terminal-window p-6 text-center">
                <p className="font-mono text-text-gray text-sm">
                  [INFO] No threat reports match the current filter.
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-5">
                {articles.map(article => (
                  <OperationsCard key={article.id} article={article} />
                ))}
              </div>
            )}
          </>
        )}

      </div>
    </div>
  )
}

/* ─────────────────────────────────────────
   Operations Card
   ───────────────────────────────────────── */
function OperationsCard({ article }: { article: Article }) {
  const accentColor = THREAT_ACCENT[article.threat_level] ?? '#2a2a2a'

  return (
    <div
      className="ops-widget group cursor-pointer hover:border-kgb-red/50 transition-all"
      style={{ borderLeftColor: accentColor }}
    >
      <div className="p-5">
        {/* Top row: source badge */}
        <div className="flex items-start justify-between mb-3">
          <span className="font-mono text-[9px] uppercase tracking-widest text-text-gray/60">
            {article.category}
          </span>
          <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-border-gray text-text-gray bg-black/40">
            {article.source_name}
          </span>
        </div>

        {/* Threat badge */}
        <div className="mb-3">
          <span className={BADGE[article.threat_level]}>
            {article.threat_level}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-display font-bold text-white text-lg leading-snug mb-3 group-hover:text-kgb-red transition-colors uppercase tracking-wide">
          {article.title}
        </h3>

        {/* Excerpt */}
        {article.excerpt && (
          <p className="text-xs text-text-gray font-mono leading-relaxed mb-4 line-clamp-2">
            {article.excerpt}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-border-gray/40">
          <div className="flex items-center gap-3">
            {article.author && (
              <span className="flex items-center gap-1 font-mono text-[10px] text-text-gray/60">
                <User size={10} />
                {article.author}
              </span>
            )}
            <span className="flex items-center gap-1 font-mono text-[10px] text-text-gray/60">
              <Calendar size={10} />
              {formatDate(article.published_at)}
            </span>
          </div>
          <a
            href={article.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-kgb-red/70 hover:text-kgb-red transition-colors"
            onClick={e => e.stopPropagation()}
          >
            READ INTEL
            <ExternalLink size={10} />
          </a>
        </div>
      </div>
    </div>
  )
}
